import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { ChevronRight, Maximize2, Minimize2 } from 'lucide-react';
import { ReactNode, Ref, SyntheticEvent } from 'react';
import { useIntl } from 'react-intl';
import DisabledHint from '~/components/Tooltips/DisabledHint';
import {
    CHROME_BG,
    consoleDividerSx,
    HAIRLINE,
    READOUT,
    READOUT_DIM,
    READOUT_FONT,
    TITLE_BAR_HEIGHT_PX,
} from '../chrome';
import {
    ONE_PLANE_ROW_MESSAGE_ID,
    PlaneCycleControl,
} from '../layout/planeCycle';
import { Plane } from '../layout/types';
import { planeLabel, planeName } from './tileViewModel';

/** A small icon button in a tile's title bar. */
export const TITLE_BAR_ICON_SX = {
    color: READOUT_DIM,
    width: 20,
    height: 20,
    p: 0.25,
    fontSize: 13,
    lineHeight: 1,
    '&:hover': { color: READOUT },
};

/** Keeps a press on a title-bar control from reaching the bar's drag handle. */
export function stopTitleBarPress(event: SyntheticEvent): void {
    event.stopPropagation();
}

/** The bar a console pane is named in, raised from it by a hairline. */
export const TITLE_BAR_SX = {
    display: 'flex',
    alignItems: 'center',
    height: TITLE_BAR_HEIGHT_PX,
    gap: '8px',
    px: '9px',
    bgcolor: CHROME_BG,
    borderBottom: 1,
    borderColor: HAIRLINE,
    flexShrink: 0,
    touchAction: 'none',
};

/** Hairline between the title bar's identity fields, matching the row footer. */
export const TITLE_BAR_DELIMITER_SX = { ...consoleDividerSx(12), mx: 0.25 };

/** The name of the plane a tile draws, in the title bar's muted readout type. */
export function TitlePlaneLabel({ plane }: { readonly plane: Plane }) {
    const intl = useIntl();
    return (
        <Typography
            component="span"
            data-testid="tile-plane"
            sx={{
                fontFamily: READOUT_FONT,
                fontSize: 11,
                letterSpacing: '0.08em',
                color: READOUT_DIM,
                flexShrink: 0,
            }}
        >
            {planeLabel(intl, plane)}
        </Typography>
    );
}

/**
 * Cycles the planes of the tile's row one step, named by the plane the tile
 * shows next and the key that does the same. Without a next plane the
 * control is disabled and says why, by hover and by Tab.
 */
export function RotatePlanesButton({ nextPlane, onCycle }: PlaneCycleControl) {
    const intl = useIntl();
    const label = nextPlane
        ? intl.formatMessage(
              { id: 'cyclePlaneTo' },
              { plane: planeName(intl, nextPlane) }
          )
        : intl.formatMessage({ id: ONE_PLANE_ROW_MESSAGE_ID });
    const enabled = nextPlane !== undefined;
    const button = (
        <IconButton
            size="small"
            aria-label={label}
            data-testid="tile-rotate-planes"
            sx={TITLE_BAR_ICON_SX}
            disabled={!enabled}
            onClick={onCycle}
        >
            <ChevronRight size={18} />
        </IconButton>
    );
    // The title bar runs across the top of the tile, so the hint opens down
    // onto the canvas.
    if (enabled) {
        return (
            <Tooltip title={label} placement="bottom">
                {button}
            </Tooltip>
        );
    }
    return (
        <DisabledHint title={label} placement="bottom">
            {button}
        </DisabledHint>
    );
}

interface TileTitleBarProps {
    /** The bar's identity fields and readouts, laid out left to right. */
    readonly children: ReactNode;
    /** Controls offered before maximize, each stopping its own press. */
    readonly controls?: ReactNode;
    readonly isMaximized?: boolean;
    /** Maximizes or restores the tile; without it the control is disabled. */
    readonly onMaximize?: () => void;
    /** Pointer listeners for the bar (the rearrange handle). */
    readonly dragHandleProps?: Record<string, unknown>;
    /** Receives the bar's element, for what hangs from its lower edge. */
    readonly barRef?: Ref<HTMLDivElement>;
}

/**
 * The title bar of one console tile: the identity fields and readouts the
 * tile names itself by, then its controls, of which maximize is the last.
 * The bar is the tile's rearrange handle for the pointer only: it is no
 * button and no Tab stop, since a pane moves by no key. A press that lands
 * on a control must not reach the drag sensor: past its activation
 * distance the gesture becomes a drag and the click never arrives.
 */
export default function TileTitleBar({
    children,
    controls,
    isMaximized = false,
    onMaximize,
    dragHandleProps,
    barRef,
}: TileTitleBarProps) {
    const intl = useIntl();
    return (
        <Box
            ref={barRef}
            {...dragHandleProps}
            sx={{
                ...TITLE_BAR_SX,
                cursor: dragHandleProps ? 'grab' : 'default',
            }}
        >
            {children}
            <Box
                sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}
                onPointerDown={stopTitleBarPress}
                onMouseDown={stopTitleBarPress}
            >
                {controls}
                {/* The glyph names the state the button leaves, so a
                    maximized pane still shows its way back. */}
                <IconButton
                    size="small"
                    aria-label={intl.formatMessage({
                        id: isMaximized ? 'tile.restore' : 'tile.maximize',
                    })}
                    sx={TITLE_BAR_ICON_SX}
                    disabled={!onMaximize}
                    onClick={onMaximize}
                >
                    {isMaximized ? (
                        <Minimize2 size={12} />
                    ) : (
                        <Maximize2 size={12} />
                    )}
                </IconButton>
            </Box>
        </Box>
    );
}
